import { useState, useEffect } from "react";
import { checkIsObjectEmpty } from "features/Common/modules/DataTypes/utils/object";
import { checkIsStringEmpty } from "features/Common/modules/DataTypes/Strings.utils";
import {
  BANNER_DETAILS,
  FIELDS_SCHEMA,
  PENDING_INVOICE_UPDATE,
  PHONE,
  PINCODE,
  REFUND_OPTIONS_CUSTOM_AMOUNT,
  REFUND_RESPONSE_FREE,
  SET_TIMEOUT_DEFAULT,
  WORKFLOWS,
} from "./EditInvoiceModal.constants";
import {
  determineWorkflow,
  getBannerInfo,
  getOriginalValues,
  validateFields,
  calculateGstDifference,
} from "./EditInvoiceModal.utils";
import {
  searchHSNCode,
  getMappedHsnFields,
} from "features/Crm/modules/Transactions/utils/hsnUtils";
import { notification_color_keys, useNotifications } from "utils/hooks";
import { api } from "data";
import { useQueryClient } from "@tanstack/react-query";
import { useRefresh } from "react-admin";
import { useWallets } from "features/Account/modules/Wallets/utils/useWallets";
import { walletKeys } from "features/Account/modules/Wallets/constants/Wallets.constants";
import { initiateJuspay } from "features/Payment/utils/Payment.utils";
import { isRequestSuccessful } from "utils/Utils";
import { setLocalStorageItem } from "utils/getterAndSetters";
import {
  initiateRefund,
  updateInvoiceDetails,
  validateRefunds,
} from "./EditInvoiceModal.api";

export const useEditInvoiceModal = (record, open, onClose) => {
  const [formData, setFormData] = useState({
    companyName: "",
    phone: "",
    email: "",
    companyAddress: "",
    pincode: "",
    state: "",
    amount: "",
    gstin: "",
    hsnCode: "",
    gstPercentage: "",
  });

  const [isReviewState, setIsReviewState] = useState(false);
  const [gstFiledAnswer, setGstFiledAnswer] = useState(null);
  const [errors, setErrors] = useState({});
  const [useWalletBalance, setUseWalletBalance] = useState(false);
  const [showHSNInput, setShowHSNInput] = useState(false);
  // isLocalSaving: Local progress indicator to block double actions.
  const [isLocalSaving, setIsLocalSaving] = useState(false);
  const [refundValidationData, setRefundValidationData] = useState(null);
  const [isValidatingRefund, setIsValidatingRefund] = useState(false);

  // pre-fetches the wallet balance for payment deduction calculation.
  const { walletBalance } = useWallets({ isFetchWalletBalanceEnabled: true });

  // Populate form defaults from record when modal opens or record changes
  useEffect(() => {
    if (open && record) {
      const originalValues = getOriginalValues(record);
      setFormData(originalValues);

      const hsn = originalValues.hsnCode;
      const hasExplicitGstPercentage =
        (record?.customer_gst_info?.gst_percentage != null &&
          record?.customer_gst_info?.gst_percentage !== "") ||
        (record?.gst_percentage != null && record?.gst_percentage !== "") ||
        (record?.creator_gst_percent != null &&
          record?.creator_gst_percent !== "");

      if (hsn && !hasExplicitGstPercentage) {
        searchHSNCode(hsn).then((hsnCodes) => {
          const matched = hsnCodes?.find(
            (item) => String(item.hsn_code) === String(hsn)
          );
          if (matched && matched.gst_percentage != null) {
            setFormData((prev) => ({
              ...prev,
              gstPercentage: String(matched.gst_percentage),
            }));
          }
        });
      }
    }
  }, [record, open]);

  // Reset internal states when the modal is closed
  useEffect(() => {
    if (!open) {
      setIsReviewState(false);
      setGstFiledAnswer(null);
      setErrors({});
      setUseWalletBalance(false);
      setShowHSNInput(false);
      setIsLocalSaving(false);
      setRefundValidationData(null);
      setIsValidatingRefund(false);
    }
  }, [open]);

  // Unified input handler. Sanitizes phone & pincode fields to prevent entering non-digits.
  const handleChange = (field, value) => {
    let cleanValue = value;
    if (field === PHONE || field === PINCODE) {
      cleanValue = value.replace(/\D/g, "");
    }
    setFormData((prev) => ({ ...prev, [field]: cleanValue }));

    // Dynamically clear existing error when user edits the invalid input
    if (errors[field]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  // Custom form validation handler
  const handleProceed = () => {
    const isCreditNoteFlow =
      workflow === WORKFLOWS.EINVOICE_FLOW || gstFiledAnswer === true;
    const validationErrors = validateFields(formData, isCreditNoteFlow);
    if (!checkIsObjectEmpty(validationErrors)) {
      setErrors(validationErrors);
      return;
    }
    setErrors({});
    setIsReviewState(true);
  };

  // Extract initial values of the record to compute diffs
  const originalValues = getOriginalValues(record);

  // Filters list of schema fields to find which ones have actual differences
  const changedFields = FIELDS_SCHEMA.filter((field) => {
    const orig = String(originalValues[field.key] || "").trim();
    const curr = String(formData[field.key] || "").trim();
    return orig !== curr;
  });

  const originalGst = Number(originalValues.gstPercentage) || 0;
  const updatedGst = Number(formData.gstPercentage) || 0;
  const totalAmount = Number(originalValues.amount) || 0;

  const { baseAmount, originalGstAmt, newGstAmt, gstDiff, isGstLower } =
    calculateGstDifference(totalAmount, originalGst, updatedGst);

  // Trigger refund validation API when entering review state with lower GST
  useEffect(() => {
    if (isReviewState && isGstLower && gstDiff > 0) {
      const fetchValidation = async () => {
        setIsValidatingRefund(true);
        try {
          const payload = {
            transaction_uuids: [record?.uuid || record?.transaction_id],
            refund_option: REFUND_OPTIONS_CUSTOM_AMOUNT,
            refund_amount: Number(gstDiff.toFixed(2)),
            amount: Number(gstDiff.toFixed(2)),
            is_gst_correction_refund: true,
            new_gst_percent: updatedGst,
          };
          const res = await validateRefunds(payload);
          if (isRequestSuccessful(res?.status)) {
            setRefundValidationData(res?.data?.data || res?.data);
          }
        } catch (err) {
          console.error("Error validating refund:", err);
        } finally {
          setIsValidatingRefund(false);
        }
      };
      fetchValidation();
    } else {
      setRefundValidationData(null);
    }
  }, [isReviewState, isGstLower, gstDiff, record]);

  const handleHsnSearchChange = (hsnOption) => {
    setFormData((prev) => ({
      ...prev,
      ...getMappedHsnFields(hsnOption, false, false),
    }));
  };

  const { notify } = useNotifications();
  const queryClient = useQueryClient();
  const refresh = useRefresh();

  const handleSaveClick = async () => {
    const isCreditNoteFlow =
      workflow === WORKFLOWS.EINVOICE_FLOW || gstFiledAnswer === true;

    const changedKeysMap = changedFields.reduce((acc, field) => {
      acc[field.key] = true;
      return acc;
    }, {});

    const updatedValues = {};

    if (changedKeysMap.companyName) {
      updatedValues.company_name = formData.companyName || "";
    }
    if (changedKeysMap.phone) {
      updatedValues.customer_phone = formData.phone || "";
    }
    if (changedKeysMap.email) {
      updatedValues.customer_email = formData.email || "";
    }
    if (changedKeysMap.companyAddress) {
      updatedValues.company_address = formData.companyAddress || "";
    }
    if (changedKeysMap.pincode) {
      updatedValues.company_pincode = formData.pincode || "";
    }
    if (changedKeysMap.state) {
      updatedValues.billing_state = formData.state || "";
    }
    if (changedKeysMap.amount) {
      updatedValues.transaction_amount = formData.amount || "";
    }
    if (changedKeysMap.gstin) {
      updatedValues.gst_number = formData.gstin || "";
    }
    if (changedKeysMap.hsnCode) {
      updatedValues.hsn_code = formData.hsnCode || "";
    }
    if (changedKeysMap.gstPercentage) {
      updatedValues.gst_percentage =
        formData.gstPercentage != null &&
        !checkIsStringEmpty(formData.gstPercentage)
          ? Number(formData.gstPercentage)
          : "";
    }

    if (isCreditNoteFlow) {
      updatedValues.is_credit_note_required = true;
    }
    if (gstFiledAnswer === true) {
      updatedValues.is_gst_filled = true;
    }
    if (isGstLower) {
      updatedValues.is_gst_correction_refund = true;
    }

    const payload = {
      transaction_uuid: record?.uuid || record?.transaction_id,
      updated_values: updatedValues,
    };

    setIsLocalSaving(true);

    try {
      if (isGstLower) {
        // 1. First, initiate the refund calculation & checkout flow if GST % decreased.
        const refundPayload = {
          transaction_uuids: [record?.uuid || record?.transaction_id],
          refund_option: REFUND_OPTIONS_CUSTOM_AMOUNT,
          return_url: `${window.location.origin}/#/payments/exly/transactions/list`,
          refund_amount: Number(totalRefundAmount.toFixed(2)),
          is_gst_correction_refund: true,
          new_gst_percent: updatedGst,
        };

        // If Use Wallet is checked, pass wallet_amount to pay the difference from wallet funds.
        if (useWalletBalance && walletBalance) {
          refundPayload.wallet_amount = walletBalance[walletKeys.walletBalance];
        }

        const refundResponse = await initiateRefund(refundPayload);

        if (isRequestSuccessful(refundResponse?.status)) {
          // Check if the difference is fully paid from wallet (gateway: "free").
          // If yes, payment is completed immediately, so save details now.
          // Otherwise, store pending update in localStorage and redirect to payment gateway.
          if (refundResponse?.data?.data?.gateway === REFUND_RESPONSE_FREE) {
            const updateResponse = await updateInvoiceDetails(payload);

            if (isRequestSuccessful(updateResponse?.status)) {
              notify(
                "Invoice details updated and refund initiated successfully.",
                notification_color_keys.success
              );
              // 1. Await query invalidation so the transactions list is fetched in the background while the loading spinner is active
              await queryClient.invalidateQueries([api.transaction_list]);
              // 2. Introduce a delay so the user has time to see and read the toast notification before the modal closes
              await new Promise((resolve) =>
                setTimeout(resolve, SET_TIMEOUT_DEFAULT)
              );
              refresh();
              setIsReviewState(false);
              if (onClose) onClose();
            } else {
              notify(
                "Refund initiated but failed to update invoice details: " +
                  (updateResponse?.data?.message || "Internal error"),
                notification_color_keys.error
              );
              await queryClient.invalidateQueries([api.transaction_list]);
              refresh();
              setIsReviewState(false);
              if (onClose) onClose();
            }
          } else {
            // Persist pending update in localStorage so it can be saved after payment succeeds upon returning from gateway
            const pendingUpdateData = {
              payload,
              timestamp: Date.now(),
            };
            setLocalStorageItem(PENDING_INVOICE_UPDATE, pendingUpdateData);

            notify(
              "Redirecting to payment gateway to complete refund...",
              notification_color_keys.info
            );
            if (refundResponse?.data?.data?.order) {
              initiateJuspay(refundResponse.data.data.order);
            } else {
              if (onClose) onClose();
            }
          }
        } else {
          notify(
            "Failed to initiate refund: " +
              (refundResponse?.data?.message || "Internal error"),
            notification_color_keys.error
          );
        }
      } else {
        // Standard success flow (no refund needed)
        const updateResponse = await updateInvoiceDetails(payload);

        if (isRequestSuccessful(updateResponse?.status)) {
          notify(
            "Invoice details updated successfully.",
            notification_color_keys.success
          );
          // 1. Await query invalidation so the transactions list is fetched in the background while the loading spinner is active
          await queryClient.invalidateQueries([api.transaction_list]);
          // 2. Introduce a delay so the user has time to see and read the toast notification before the modal closes
          await new Promise((resolve) =>
            setTimeout(resolve, SET_TIMEOUT_DEFAULT)
          );
          refresh();
          setIsReviewState(false);
          if (onClose) onClose();
        } else {
          notify(
            updateResponse?.data?.message ||
              "Failed to update invoice details.",
            notification_color_keys.error
          );
        }
      }
    } catch (err) {
      notify(
        err?.response?.data?.message ||
          err?.message ||
          "Something went wrong while saving.",
        notification_color_keys.error
      );
    } finally {
      setIsLocalSaving(false);
    }
  };

  const workflow = determineWorkflow(record);
  let banner = getBannerInfo(workflow);
  if (gstFiledAnswer === true) {
    banner = {
      text: BANNER_DETAILS.text,
      className: BANNER_DETAILS.className,
    };
  }

  const totalRefundAmount = refundValidationData
    ? parseFloat(refundValidationData.refund_amount || 0) +
      parseFloat(refundValidationData.rihf_fees_exclusive_of_tx ?? 0) +
      parseFloat(refundValidationData.tax_amount || 0)
    : gstDiff;

  return {
    formData,
    handleChange,
    isReviewState,
    setIsReviewState,
    originalValues,
    changedFields,
    handleSaveClick,
    workflow,
    banner,
    gstFiledAnswer,
    setGstFiledAnswer,
    isSaving: isLocalSaving,
    errors,
    handleProceed,
    setFormData,
    useWalletBalance,
    setUseWalletBalance,
    walletBalance,
    originalGst,
    updatedGst,
    totalAmount,
    baseAmount,
    originalGstAmt,
    newGstAmt,
    gstDiff,
    totalRefundAmount,
    isGstLower,
    searchHSNCode,
    handleHsnSearchChange,
    showHSNInput,
    setShowHSNInput,
    refundValidationData,
    isValidatingRefund,
  };
};
